import { sanitizeViewportSettings } from './coordinate-utils';
import { AqiStandardId, DatasourceId, NetworkId, OpenSiteId, Point } from '../types';
import { PreviewToken } from './data-preview';
import { ISettings } from './settings-interface';
import { SettingsKey } from './settings-key';

export class SavedSettings implements ISettings {
  KEY_PREFIX = 'wlom:settings';
  private _latitude: number | null;
  private _longitude: number | null;
  private _zoom: number | null;
  private _searchedLatitude: number | null;
  private _searchedLongitude: number | null;

  constructor(private readonly disabled = false) {
    // Initialize viewport coordinates
    const latStr = this.get(SettingsKey.latitude);
    const lngStr = this.get(SettingsKey.longitude);
    const zoomStr = this.get(SettingsKey.zoom);

    const lat = latStr ? parseFloat(latStr) : null;
    const lng = lngStr ? parseFloat(lngStr) : null;
    const zoom = zoomStr ? parseFloat(zoomStr) : null;

    const sanitized = sanitizeViewportSettings(lat, lng, zoom);
    this._latitude = sanitized.latitude;
    this._longitude = sanitized.longitude;
    this._zoom = sanitized.zoom;

    // Initialize searched coordinates
    const searchedLatStr = this.get(SettingsKey.searchedLatitude);
    const searchedLngStr = this.get(SettingsKey.searchedLongitude);

    const searchedLat = searchedLatStr ? parseFloat(searchedLatStr) : null;
    const searchedLng = searchedLngStr ? parseFloat(searchedLngStr) : null;

    const searchedSanitized = sanitizeViewportSettings(searchedLat, searchedLng, null);
    this._searchedLatitude = searchedSanitized.latitude;
    this._searchedLongitude = searchedSanitized.longitude;
  }

  get(key: SettingsKey) {
    return localStorage.getItem(`${this.KEY_PREFIX}:${key}`);
  }

  set(key: SettingsKey, value: string) {
    if (!this.disabled) {
      localStorage.setItem(`${this.KEY_PREFIX}:${key}`, value);
    }
  }

  remove(key: SettingsKey) {
    if (!this.disabled) {
      localStorage.removeItem(`${this.KEY_PREFIX}:${key}`);
    }
  }

  clear() {
    Object.values(SettingsKey).forEach((key) => {
      localStorage.removeItem(`${this.KEY_PREFIX}:${key}`);
    });
  }

  get networkId(): NetworkId | null {
    return this.get(SettingsKey.networkId) as NetworkId | null;
  }

  get latitude(): number | null {
    return this._latitude;
  }

  get longitude(): number | null {
    return this._longitude;
  }

  get zoom(): number | null {
    return this._zoom;
  }

  get datasourceId(): DatasourceId | null {
    return this.get(SettingsKey.datasourceId) as DatasourceId | null;
  }

  get searchedLatitude(): number | null {
    return this._searchedLatitude;
  }

  get searchedLongitude(): number | null {
    return this._searchedLongitude;
  }

  get searchedLocation(): Point | null {
    if (this.searchedLatitude && this.searchedLongitude) {
      return {
        type: 'Point',
        coordinates: [this.searchedLongitude, this.searchedLatitude],
      };
    }
    return null;
  }

  get aqiStdId(): AqiStandardId | null {
    return this.get(SettingsKey.aqiStdId) as AqiStandardId | null;
  }

  get preview(): PreviewToken | null {
    return null;
  }

  get openSiteId(): OpenSiteId | null {
    return this.get(SettingsKey.openSiteId) as OpenSiteId | null;
  }

  set networkId(value: NetworkId) {
    this.set(SettingsKey.networkId, value);
  }

  set latitude(value: number) {
    this.set(SettingsKey.latitude, value.toString());
  }

  set longitude(value: number) {
    this.set(SettingsKey.longitude, value.toString());
  }

  set zoom(value: number) {
    this.set(SettingsKey.zoom, value.toString());
  }

  set datasourceId(value: DatasourceId | null) {
    if (value === null) {
      this.remove(SettingsKey.datasourceId);
    } else {
      this.set(SettingsKey.datasourceId, value);
    }
  }

  set searchedLocation(value: Point | null) {
    if (value === null) {
      this.remove(SettingsKey.searchedLatitude);
      this.remove(SettingsKey.searchedLongitude);
    } else {
      this.set(SettingsKey.searchedLatitude, value.coordinates[1].toString());
      this.set(SettingsKey.searchedLongitude, value.coordinates[0].toString());
    }
  }

  set aqiStdId(value: AqiStandardId | null) {
    if (value === null) {
      this.remove(SettingsKey.aqiStdId);
    } else {
      this.set(SettingsKey.aqiStdId, value);
    }
  }

  set openSiteId(value: OpenSiteId | null) {
    if (value === null) {
      this.remove(SettingsKey.openSiteId);
    } else {
      this.set(SettingsKey.openSiteId, value);
    }
  }

  isEmpty(): boolean {
    return Object.values(SettingsKey).every((key) => !this.get(key));
  }
}
